import React, { useEffect, useId, useRef, useState } from "react";
import { RosterPatient, RoomSaveResult, patientName, providerApi } from "../providerApi";

/**
 * The resident's room, as a box the provider can correct (provider Eric Parker / Isaiah Cotto, 2026-10-21;
 * backend migration 20261022a, docs/provider/ROOM_OVERRIDE.md).
 *
 * Monday's room (patients.room_number) is never changed: what is saved is eMerge's own room for the resident, shown
 * wherever the room is shown. Clearing the box goes back to Monday's room. The box is offered only where the server
 * said it would accept the save (`provenance.writableFields` names `room`, and the resident is care-assigned);
 * everywhere else the room is plain text.
 *
 * Saves on Enter or when the box loses focus; Escape puts the last saved room back. The new room shows at once and
 * says "Saving…"; if the server refuses, the room goes back and the reason is said. A room the provider changed
 * carries an "edited" mark naming Monday's room.
 */

const ROOM_MAX = 20;

/** The same folding the server does: trimmed, inner whitespace as one space. */
const fold = (v: string) => v.replace(/\s+/g, " ").trim();

export type RoomView = Pick<RosterPatient, "room_number" | "room_edited" | "room_monday">;

/** The small "edited" mark, with Monday's room in its tooltip and its accessible text. */
export function RoomEditedMark({ monday, id }: { monday: string | null | undefined; id?: string }) {
  const words = `Changed in eMerge. The manifest room from Monday is ${monday ? monday : "not recorded"}.`;
  return (
    <span className="fw-room-edited" title={words} id={id}>
      edited<span className="pv-sr-only"> — {words}</span>
    </span>
  );
}

type Phase = { kind: "idle" } | { kind: "saving" } | { kind: "saved" } | { kind: "error"; message: string };

export function RoomField({
  facilityId,
  patient,
  editable,
  offline,
  onSaved,
}: {
  facilityId: string;
  patient: Pick<RosterPatient, "id" | "first_name" | "last_name" | "room_number" | "room_edited" | "room_monday">;
  editable: boolean;
  offline: boolean;
  onSaved?: (patientId: string, room: RoomView) => void;
}) {
  const [shown, setShown] = useState<RoomView>({
    room_number: patient.room_number,
    room_edited: Boolean(patient.room_edited),
    room_monday: patient.room_monday === undefined ? patient.room_number : patient.room_monday,
  });
  const [draft, setDraft] = useState(patient.room_number || "");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const focused = useRef(false);
  const saving = useRef(false);
  const markId = useId();
  const name = patientName(patient);

  // A newer day sheet (a refresh) replaces what is shown, unless the provider is typing or a save is in flight.
  useEffect(() => {
    if (focused.current || saving.current) return;
    const next = {
      room_number: patient.room_number,
      room_edited: Boolean(patient.room_edited),
      room_monday: patient.room_monday === undefined ? patient.room_number : patient.room_monday,
    };
    setShown(next);
    setDraft(next.room_number || "");
  }, [patient.room_number, patient.room_edited, patient.room_monday]);

  if (!editable) {
    return (
      <span className="fw-room">
        Room {shown.room_number || "not recorded"}
        {shown.room_edited && (
          <>
            {" "}
            <RoomEditedMark monday={shown.room_monday} />
          </>
        )}
      </span>
    );
  }

  const commit = async () => {
    if (saving.current) return;
    const wanted = fold(draft);
    const current = shown.room_number || "";
    // Nothing changed, or an empty box over Monday's own room (nothing to clear).
    if (wanted === current || (wanted === "" && !shown.room_edited)) {
      setDraft(current);
      return;
    }
    if (offline) {
      setPhase({ kind: "error", message: "You are offline. The room was not saved." });
      setDraft(current);
      return;
    }
    if (wanted.length > ROOM_MAX) {
      setPhase({ kind: "error", message: `A room is at most ${ROOM_MAX} characters. Nothing was saved.` });
      return;
    }
    const before = shown;
    // Optimistic: the new room shows at once; an empty box shows Monday's again.
    const monday = shown.room_monday ?? null;
    const optimistic: RoomView = wanted === "" || wanted === (monday || "")
      ? { room_number: monday, room_edited: false, room_monday: monday }
      : { room_number: wanted, room_edited: true, room_monday: monday };
    setShown(optimistic);
    setDraft(optimistic.room_number || "");
    setPhase({ kind: "saving" });
    saving.current = true;
    try {
      const r = await providerApi.setRoom(facilityId, patient.id, wanted);
      if (r.success && r.data) {
        const d: RoomSaveResult = r.data;
        const saved: RoomView = { room_number: d.room_number, room_edited: d.room_edited, room_monday: d.room_monday };
        setShown(saved);
        if (!focused.current) setDraft(saved.room_number || "");
        setPhase({ kind: "saved" });
        if (onSaved) onSaved(patient.id, saved);
      } else {
        setShown(before);
        setDraft(before.room_number || "");
        setPhase({ kind: "error", message: r.message || r.error || "The room was not saved." });
      }
    } catch {
      setShown(before);
      setDraft(before.room_number || "");
      setPhase({ kind: "error", message: "Could not reach the server. The room was not saved." });
    } finally {
      saving.current = false;
    }
  };

  const inputId = `room-${patient.id}`;
  const status =
    phase.kind === "saving" ? "Saving…" : phase.kind === "saved" ? "Saved" : "";
  return (
    <span className="fw-room fw-room-editable">
      <label htmlFor={inputId}>Room</label>{" "}
      <input
        id={inputId}
        className="fw-room-input"
        type="text"
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        maxLength={ROOM_MAX}
        value={draft}
        placeholder="not recorded"
        aria-label={`Room for ${name}`}
        aria-describedby={shown.room_edited ? markId : undefined}
        aria-invalid={phase.kind === "error" ? true : undefined}
        readOnly={offline}
        title={offline ? "You are offline; the room cannot be saved now." : "Type the room, then press Enter. Clear it to use the manifest room from Monday."}
        onFocus={() => { focused.current = true; }}
        onChange={(e) => {
          setDraft(e.target.value);
          if (phase.kind !== "saving") setPhase({ kind: "idle" });
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setDraft(shown.room_number || "");
            setPhase({ kind: "idle" });
          }
        }}
        onBlur={() => {
          focused.current = false;
          void commit();
        }}
      />
      {shown.room_edited && (
        <>
          {" "}
          <RoomEditedMark monday={shown.room_monday} id={markId} />
        </>
      )}
      <span role="status" aria-live="polite" className="fw-room-status">
        {status}
      </span>
      {phase.kind === "error" && (
        <span role="alert" className="fw-error fw-room-error">
          {phase.message}
        </span>
      )}
    </span>
  );
}
